"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Checkbox } from "../ui/checkbox"
import { Save, Loader2 } from "lucide-react"
import { apiClient } from "../lib/apiClient"
import { useToast } from "../hooks/use-toast"

interface Menu {
  menuID: number
  menuName: string
}

interface RoleMenuMapping {
  roleID: number
  roleName: string
  menus: Menu[]
}

interface AddEditRoleMenuMappingModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  initialData?: RoleMenuMapping | null
  mode: "add" | "edit"
  allMenus?: Menu[]
}

export function AddEditRoleMenuMappingModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  mode,
  allMenus = [],
}: AddEditRoleMenuMappingModalProps) {
  const [roleName, setRoleName] = useState("")
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ roleName?: string; menus?: string }>({})
  const { toast } = useToast()

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && initialData) {
        // Edit mode: populate with existing data
        setRoleName(initialData.roleName)
        setSelectedMenuIds(initialData.menus?.map((m) => m.menuID) || [])
      } else {
        // Add mode: reset form
        setRoleName("")
        setSelectedMenuIds([])
      }
      setErrors({})
    }
  }, [isOpen, mode, initialData])

  const handleMenuToggle = (menuID: number) => {
    setSelectedMenuIds((prev) => {
      if (prev.includes(menuID)) {
        return prev.filter((id) => id !== menuID)
      } else {
        return [...prev, menuID]
      }
    })
    // Clear error when user selects a menu
    if (errors.menus) {
      setErrors((prev) => ({ ...prev, menus: undefined }))
    }
  }

  const validate = () => {
    const newErrors: { roleName?: string; menus?: string } = {}
    
    if (mode === "add" && !roleName.trim()) {
      newErrors.roleName = "Role name is required"
    }
    
    if (selectedMenuIds.length === 0) {
      newErrors.menus = "At least one menu must be selected"
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return

    try {
      setLoading(true)

      const requestData = {
        RoleID: mode === "edit" && initialData ? initialData.roleID : null,
        RoleName: mode === "edit" && initialData ? initialData.roleName : roleName.trim(),
        MenuIDs: selectedMenuIds.join(","),
      }

      const response = await apiClient.post("/Admin/role-menu-mapping", requestData)
      
      toast({
        title: "Success",
        description: response.data?.message || (mode === "add" ? "Role created successfully" : "Role updated successfully"),
        duration: 3000,
        className: "border border-green-300",
      })

      onSave()
      onClose()
    } catch (error: any) {
      console.error("Error saving role-menu mapping:", error)
      toast({
        title: "Error",
        description: error?.response?.data?.message || error?.message || "Failed to save role-menu mapping",
        duration: 4000,
        className: "border border-red-300",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-white border border-gray-200 rounded-lg shadow-lg p-0 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6">
          <DialogTitle className="text-xl font-bold text-gray-900">
            {mode === "add" ? "Add New Role" : `Edit Menu Mapping - ${initialData?.roleName}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-6 px-6">
          {/* Role Name - Only editable in add mode */}
          {mode === "add" ? (
            <div className="space-y-2">
              <Label htmlFor="roleName" className="text-sm font-medium text-gray-700">
                Role Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="roleName"
                value={roleName}
                onChange={(e) => {
                  setRoleName(e.target.value)
                  if (errors.roleName) {
                    setErrors((prev) => ({ ...prev, roleName: undefined }))
                  }
                }}
                placeholder="Enter role name"
                className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.roleName && <p className="text-xs text-red-600">{errors.roleName}</p>}
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Role Name</Label>
              <div className="text-gray-900 font-semibold min-h-[40px] flex items-center px-3 py-2 border border-gray-200 rounded-md bg-gray-50">
                {initialData?.roleName}
              </div>
            </div>
          )}

          {/* Menu Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Select Menus <span className="text-red-500">*</span>
            </Label>
            {allMenus.length === 0 ? (
              <div className="text-gray-500 text-sm py-4 text-center">
                No menus available
              </div>
            ) : (
              <div className="border border-gray-200 rounded-md p-4 max-h-64 overflow-y-auto bg-gray-50">
                <div className="grid grid-cols-2 gap-3">
                  {allMenus.map((menu) => (
                    <div
                      key={menu.menuID}
                      className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-md cursor-pointer"
                      onClick={() => handleMenuToggle(menu.menuID)}
                    >
                      <Checkbox
                        checked={selectedMenuIds.includes(menu.menuID)}
                        onCheckedChange={() => handleMenuToggle(menu.menuID)}
                        id={`menu-${menu.menuID}`}
                      />
                      <Label
                        htmlFor={`menu-${menu.menuID}`}
                        className="text-sm font-medium text-gray-700 cursor-pointer flex-1"
                      >
                        {menu.menuName}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {errors.menus && <p className="text-xs text-red-600">{errors.menus}</p>}
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-2.5"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white px-6 py-2.5 text-base font-medium rounded-md shadow-sm"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

